import FormSubmitProps from "@/src/types/FormSubmitProps";
import wizardStyle from "@/src/components/Form/wizardStyle";
import React, { useEffect, useState } from "react";
import { dateFieldDefault, isPhoneNumber, isTodayOrPastDate, minStringLength, requiredValidator } from "@/src/services/validationRules";
import { Box, Paper, Typography } from "@mui/material";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import { useForm } from "@/src/provider/Form/FormProvider";
import AdvisoryIcon from "@/src/assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "@/src/components/Form/Common/Person/Person";
import Grid from "@mui/material/Grid";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import Group from "@/src/components/Wizard/Group";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import EnforceIcon from "@/src/assets/icons/contract/toggleable/IconEnforce";
import DefendIcon from "@/src/assets/icons/contract/toggleable/IconDefend";
import Address from "@/src/components/Form/Common/Address/Address";
import PersonIcon from "@/src/assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "@/src/assets/icons/generic/toggleable/IconFamilyDetailed";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import { AbstractCase, CaseCategorySelectionMode } from "@/src/types/Entities/AbstractCase";
import CoinsuredClient from "@/src/components/Form/AbstractLitigationCase/CoinsuredClient";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { translate } from "@/src/services/translations/translations";
import EmploymentContractBaseDefendClaim from "./EmploymentContractBaseDefendClaim";
import EmploymentContractBaseEnforceClaim from "./EmploymentContractBaseEnforceClaim";
import IconCompany from "@/src/assets/icons/contract/toggleable/IconCompany";
import IconHouse from "@/src/assets/icons/provision/toggleable/IconHouse";
import IconQuestionmark from "@/src/assets/icons/common/toggleable/IconQuestionmark";
import InfoIconButton from "@/src/components/Button/InfoIconButton";
import DialogModal from "@/src/components/DialogModal/DialogModal";
import InputAdornment from "@mui/material/InputAdornment";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";

type EmploymentContractCaseSelectionProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
  hideCaseCategories?: boolean;
};

export default function EmploymentContractBase({ product, submitProps, hideCaseCategories = false }: EmploymentContractCaseSelectionProps) {
  const { handleSubmit, values, handleChange } = useForm();
  const [infoModalOpen, setInfoModalOpen] = useState("");
  const shouldHideCaseCategories = hideCaseCategories || product.caseCategorySelectionMode === CaseCategorySelectionMode.FIXED;

  useEffect(() => {
    if (values.affectedPartyType !== "coinsured") {
      handleChange({
        target: {
          name: "client.gender",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.givenName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.familyName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.birthDate",
          value: null,
        },
      });
      handleChange({
        target: {
          name: "clientWasMinorAtContractDate",
          value: null,
        },
      });
      return;
    }
  }, [values.affectedPartyType]);

  useEffect(() => {
    if (values.employmentRegularWorkplace !== "other") {
      handleChange({
        target: {
          name: "employmentRegularWorkplaceOtherAddress.streetAddress",
          value: null,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "employmentRegularWorkplaceOtherAddress.postalCode",
          value: null,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "employmentRegularWorkplaceOtherAddress.addressLocality",
          value: null,
          type: "text",
        },
      });
    }
  }, [values.employmentRegularWorkplace]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: translate("employmentContract.caseCategory.options.enforceClaim.label", values),
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText: translate("employmentContract.caseCategory.options.enforceClaim.optionHintText", values),
      },
      {
        labelText: translate("employmentContract.caseCategory.options.defendClaim.label", values),
        labelIcon: <DefendIcon />,
        labelIconActive: <DefendIcon active />,
        value: "defendClaim",
        optionHintText: translate("employmentContract.caseCategory.options.defendClaim.optionHintText", values),
      },
    ];

    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options.unshift({
        labelText: translate("employmentContract.caseCategory.options.advisory.label", values),
        labelIcon: <AdvisoryIcon />,
        labelIconActive: <AdvisoryIcon active />,
        value: "advisory",
        optionHintText: translate("employmentContract.caseCategory.options.advisory.optionHintText", values),
      });
    }

    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Vertragsart
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Allgemeines zu Ihrem Arbeitsvertrag</strong>
              </FormLegend>
            </Grid>
            {!shouldHideCaseCategories && (
              <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question={translate("employmentContract.caseCategory.label", values)}
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte geben Sie die Art des Vertrags an.")]}
                />
              </Grid>
            )}

            {values.caseCategory && (
              <>
                <Person path="customer.person" headline={"Wie heißen Sie?"} />

                {["enforceClaim", "defendClaim"].includes(values.caseCategory) && (
                  <Address path="customer.person.residenceAddress" headline={"Wo wohnen Sie?"} />
                )}

                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>Wie können wir Sie erreichen?</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="customer.telephone"
                    label="Ihre Telefonnummer"
                    autoComplete="tel"
                    validators={[
                      requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                      {
                        validator: minStringLength,
                        params: [7],
                        message: "Ihre Telefonnummer ist zu kurz",
                      },
                      { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                    ]}
                  />
                </Grid>
                {["enforceClaim", "defendClaim"].includes(values.caseCategory) && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <Group
                        type="radio"
                        name="affectedPartyType"
                        question={translate("employmentContract.affectedPartyType.question", values)}
                        options={[
                          {
                            labelText: translate("employmentContract.affectedPartyType.options.customer.label", values),
                            labelIcon: <PersonIcon />,
                            labelIconActive: <PersonIcon active />,
                            value: "customer",
                          },
                          {
                            labelText: translate("employmentContract.affectedPartyType.options.coinsured.label", values),
                            labelIcon: <FamilyIcon />,
                            labelIconActive: <FamilyIcon active />,
                            value: "coinsured",
                          },
                        ]}
                        validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                      />
                    </Grid>

                    {values.affectedPartyType === "coinsured" && <CoinsuredClient product={product} />}

                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <Group
                        type="radio"
                        name="currentlyEmployed"
                        question={translate("employmentContract.currentlyEmployed.question", values)}
                        options={[
                          {
                            labelText: translate("employmentContract.currentlyEmployed.options.yes.label", values),
                            labelIcon: <CheckIcon />,
                            labelIconActive: <CheckIcon active />,
                            value: "yes",
                          },
                          {
                            labelText: translate("employmentContract.currentlyEmployed.options.no.label", values),
                            labelIcon: <CrossIcon />,
                            labelIconActive: <CrossIcon active />,
                            value: "no",
                          },
                        ]}
                        validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                      />
                    </Grid>

                    {values.currentlyEmployed && (
                      <>
                        <NumberFieldConfigured caseData={values} fieldPath={"grossSalary"} fieldType={"money"} numberType={"float"} />

                        <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                          <FormLegend>{translate("employmentContract.employmentStartDate.question", values)}</FormLegend>
                        </Grid>
                        <Grid size={12}>
                          <ValidatorDateField
                            name={"employmentStartDate"}
                            label={translate("employmentContract.employmentStartDate.label", values)}
                            validators={[
                              requiredValidator("Bitte geben Sie an, wann das Arbeitsverhältnis mit dem Arbeitgeber begonnen wurde"),
                              dateFieldDefault,
                              {
                                validator: isTodayOrPastDate,
                                message: "Das Datum darf nicht in der Zukunft liegen.",
                              },
                            ]}
                          />
                        </Grid>
                      </>
                    )}

                    {values.currentlyEmployed === "no" && (
                      <>
                        <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                          <FormLegend>{translate("employmentContract.employmentEndDate.question", values)}</FormLegend>
                        </Grid>
                        <Grid size={12}>
                          <ValidatorDateField name={"employmentEndDate"} label={translate("employmentContract.employmentEndDate.label", values)} />
                        </Grid>
                      </>
                    )}

                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>
                        {translate("employmentContract.employmentRegularWorkplace.question", values)}
                        <InfoIconButton onClick={() => setInfoModalOpen("regularWorkplace")} />
                      </FormLegend>
                    </Grid>

                    <Grid size={12}>
                      <Group
                        type="radio"
                        name="employmentRegularWorkplace"
                        question={""}
                        options={[
                          {
                            labelText: translate("employmentContract.employmentRegularWorkplace.options.company.label", values),
                            labelIcon: <IconCompany />,
                            labelIconActive: <IconCompany active />,
                            value: "company",
                            optionHintText: translate("employmentContract.employmentRegularWorkplace.options.company.optionHintText", values),
                          },
                          {
                            labelText: translate("employmentContract.employmentRegularWorkplace.options.home.label", values),
                            labelIcon: <IconHouse />,
                            labelIconActive: <IconHouse active />,
                            value: "home",
                            optionHintText: translate("employmentContract.employmentRegularWorkplace.options.home.optionHintText", values),
                          },
                          {
                            labelText: translate("employmentContract.employmentRegularWorkplace.options.other.label", values),
                            labelIcon: <IconQuestionmark />,
                            labelIconActive: <IconQuestionmark active />,
                            value: "other",
                            optionHintText: translate("employmentContract.employmentRegularWorkplace.options.other.optionHintText", values),
                          },
                        ]}
                        validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                      />
                    </Grid>

                    {values.employmentRegularWorkplace === "other" && (
                      <>
                        <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                          <FormLegend>{translate("employmentContract.employmentRegularWorkplaceOtherAddressDescription.question", values)}</FormLegend>
                        </Grid>
                        <Grid size={12}>
                          <ValidatorTextField
                            name={"employmentRegularWorkplaceOtherAddressDescription"}
                            label={translate("employmentContract.employmentRegularWorkplaceOtherAddressDescription.label", values)}
                            validators={[requiredValidator()]}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <InfoIconButton onClick={() => setInfoModalOpen("workplaceOtherAddressDescription")} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Address path="employmentRegularWorkplaceOtherAddress" headline={""} />
                      </>
                    )}
                  </>
                )}

                {values.caseCategory === "enforceClaim" && <EmploymentContractBaseEnforceClaim product={product} />}
                {values.caseCategory === "defendClaim" && <EmploymentContractBaseDefendClaim product={product} />}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
      <DialogModal open={infoModalOpen === "regularWorkplace"} onClose={() => setInfoModalOpen("")} title={"Info zu regelmäßigem Arbeitsort"}>
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          Der <strong>gewöhnliche Arbeitsort</strong> ist der Ort, an dem Sie den überwiegenden Teil Ihrer Arbeitszeit verbringen. Er gilt als der Mittelpunkt
          Ihrer beruflichen Tätigkeit und ist entscheidend für die Frage, wo die Arbeit tatsächlich geleistet wird.
        </Typography>
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          Dieser Ort muss nicht zwingend die Hauptzentrale Ihres Unternehmens sein. Er kann auch Ihr <strong>Homeoffice</strong> sein, wenn Sie dort regelmäßig
          den Großteil Ihrer Arbeit erledigen. Ebenso kann es ein anderer fester Ort sein, den Sie regelmäßig aufsuchen, um von dort aus Ihre Arbeit zu
          organisieren und zu beginnen.
        </Typography>
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          Die korrekte Bestimmung des gewöhnlichen Arbeitsortes hat wichtige rechtliche Bedeutung, unter anderem für die Zuständigkeit von Gerichten.
        </Typography>
      </DialogModal>
      <DialogModal open={infoModalOpen === "workplaceOtherAddressDescription"} onClose={() => setInfoModalOpen("")} title={"Arbeitsort"}>
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          Beschreiben Sie hier kurz, um was für einen Arbeitsort es sich handelt. Beispielsweise eine Zweigstelle Ihres Arbeitgebers oder ein Coworking-Space.
        </Typography>
      </DialogModal>
    </Paper>
  );
}
